import { useMemo, useState, type ReactNode } from "react";
import { Pagination } from "../components/search/Pagination";

/** A column definition; supplying {@code sortValue} makes the header sortable. */
export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  sortValue?: (row: T) => string | number;
  className?: string;
}

/** A bulk action applied to the currently selected rows. */
export interface BulkAction<T> {
  label: string;
  run: (rows: T[]) => Promise<void>;
  danger?: boolean;
  confirm?: string;
}

export interface TypeFilter<T> {
  label: string;
  value: (row: T) => string;
  options: { value: string; label: string }[];
}

interface AdminTableProps<T> {
  rows: T[];
  getId: (row: T) => number;
  columns: Column<T>[];
  /** Concatenated text used by the filter box for each row. */
  searchText: (row: T) => string;
  searchPlaceholder?: string;
  typeFilter?: TypeFilter<T>;
  /** More drop-down filters beside {@code typeFilter} (e.g. provider and visibility). */
  filters?: TypeFilter<T>[];
  bulkActions?: BulkAction<T>[];
  /** Extra toolbar buttons, shown before the create button. */
  toolbarExtra?: ReactNode;
  /** Where the row's "view" link goes (the public page for the item). */
  viewHref?: (row: T) => string;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => Promise<void>;
  onCreate?: () => void;
  createLabel?: string;
  loading?: boolean;
  error?: string;
  emptyLabel?: string;
  pageSize?: number;
}

type SortDir = "asc" | "desc";

/**
 * A self-contained admin table. Data is fetched by the parent and handed in
 * whole; filtering, type-faceting, sorting, pagination, row selection, and bulk
 * actions all happen here on the client. Row edits/deletes and bulk actions are
 * delegated to the parent (which performs the API call and reloads its data).
 */
export function AdminTable<T>(props: AdminTableProps<T>) {
  const {
    rows,
    getId,
    columns,
    searchText,
    searchPlaceholder = "Filter…",
    typeFilter,
    filters: extraFilters = [],
    bulkActions = [],
    toolbarExtra,
    viewHref,
    onEdit,
    onDelete,
    onCreate,
    createLabel = "+ New",
    loading,
    error,
    emptyLabel = "Nothing here yet.",
    pageSize = 20,
  } = props;

  const [query, setQuery] = useState("");
  const filters = useMemo(
    () => (typeFilter ? [typeFilter, ...extraFilters] : extraFilters),
    [typeFilter, extraFilters],
  );
  /** Selected value per filter, by label; "ALL" means unfiltered. */
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rows;
    if (q) list = list.filter((r) => searchText(r).toLowerCase().includes(q));
    for (const f of filters) {
      const want = chosen[f.label] ?? "ALL";
      if (want !== "ALL") list = list.filter((r) => f.value(r) === want);
    }
    const col = columns.find((c) => c.key === sortKey);
    if (col?.sortValue) {
      const sv = col.sortValue;
      list = [...list].sort((a, b) => {
        const av = sv(a);
        const bv = sv(b);
        const cmp =
          typeof av === "number" && typeof bv === "number"
            ? av - bv
            : String(av).localeCompare(String(bv));
        return sortDir === "asc" ? cmp : -cmp;
      });
    }
    return list;
  }, [rows, query, filters, chosen, columns, sortKey, sortDir, searchText]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const clampedPage = Math.min(page, totalPages - 1);
  const pageRows = filtered.slice(clampedPage * pageSize, clampedPage * pageSize + pageSize);

  const selectedRows = filtered.filter((r) => selected.has(getId(r)));
  const allSelected = filtered.length > 0 && selectedRows.length === filtered.length;
  const hasRowActions = !!onEdit || !!onDelete || !!viewHref;

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected(() => (allSelected ? new Set<number>() : new Set(filtered.map(getId))));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  async function runBulk(action: BulkAction<T>) {
    if (action.confirm && !window.confirm(action.confirm)) return;
    setBusy(true);
    setActionError("");
    try {
      await action.run(selectedRows);
      clearSelection();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : `${action.label} failed.`);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(row: T) {
    if (!onDelete) return;
    if (!window.confirm("Delete this item? When write-through is on, its backing file is removed too.")) {
      return;
    }
    setBusy(true);
    setActionError("");
    try {
      await onDelete(row);
      setSelected((prev) => {
        const n = new Set(prev);
        n.delete(getId(row));
        return n;
      });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Delete failed.");
    } finally {
      setBusy(false);
    }
  }

  function sortBy(col: Column<T>) {
    if (!col.sortValue) return;
    if (sortKey === col.key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir("asc");
    }
  }

  return (
    <div>
      <div className="admin__toolbar">
        <input
          type="text"
          value={query}
          placeholder={searchPlaceholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(0);
          }}
        />
        {filters.map((f) => (
          <label key={f.label} className="row" style={{ gap: "0.3rem" }}>
            <span className="mono" style={{ fontSize: "0.76rem" }}>
              {f.label}:
            </span>
            <select
              value={chosen[f.label] ?? "ALL"}
              onChange={(e) => {
                setChosen((prev) => ({ ...prev, [f.label]: e.target.value }));
                setPage(0);
              }}
            >
              <option value="ALL">All</option>
              {f.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ))}
        <span style={{ flex: 1 }} />
        <span className="admin__count">
          {loading ? "Loading…" : `${filtered.length} of ${rows.length}`}
        </span>
        {toolbarExtra}
        {onCreate && (
          <button className="btn" onClick={onCreate}>
            {createLabel}
          </button>
        )}
      </div>

      {selectedRows.length > 0 && (
        <div className="admin__bulk">
          <strong>{selectedRows.length} selected</strong>
          {bulkActions.map((a) => (
            <button
              key={a.label}
              className="btn btn--ghost"
              disabled={busy}
              onClick={() => runBulk(a)}
            >
              {a.label}
            </button>
          ))}
          <button className="admin-link-btn" onClick={clearSelection}>
            clear
          </button>
        </div>
      )}

      {actionError && (
        <p className="notice notice--err" role="alert">
          ✕ {actionError}
        </p>
      )}

      {error ? (
        <p className="notice notice--err">✕ {error}</p>
      ) : filtered.length === 0 ? (
        <p className="admin-empty">{loading ? "Loading…" : emptyLabel}</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th style={{ width: "1.6rem" }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label="Select all rows"
                />
              </th>
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={(c.sortValue ? "sortable " : "") + (c.className ?? "")}
                  onClick={() => sortBy(c)}
                >
                  {c.header}
                  {sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
              {hasRowActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => {
              const id = getId(row);
              const sel = selected.has(id);
              return (
                <tr key={id} className={sel ? "is-selected" : undefined}>
                  <td>
                    <input
                      type="checkbox"
                      checked={sel}
                      onChange={() => toggle(id)}
                      aria-label="Select row"
                    />
                  </td>
                  {columns.map((c) => (
                    <td key={c.key} className={c.className}>
                      {c.render(row)}
                    </td>
                  ))}
                  {hasRowActions && (
                    <td className="col-actions">
                      {viewHref && (
                        <a href={viewHref(row)} target="_blank" rel="noopener noreferrer">
                          view
                        </a>
                      )}
                      {viewHref && (onEdit || onDelete) && " · "}
                      {onEdit && (
                        <button className="admin-link-btn" onClick={() => onEdit(row)}>
                          edit
                        </button>
                      )}
                      {onEdit && onDelete && " · "}
                      {onDelete && (
                        <button
                          className="admin-link-btn admin-link-btn--danger"
                          onClick={() => handleDelete(row)}
                        >
                          delete
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Pagination page={clampedPage} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}
